import React, { useState, useEffect, useCallback, useMemo } from "react";
import TopBar from "../../../components/template/TopBar/TopBar";
import { useNavigate } from "react-router-dom";
import PullToRefresh from "react-simple-pull-to-refresh";
import { InboundItem, MediaItem } from "../../../types/item";
import MediaUploadSection from "./media/index.tsx";
import MediaViewer from "./mediaGallery/mediaViewer";
import ConfirmModal from "../../../components/template/ConfirmModal/ConfirmModal";
import { compressImage } from "../../../lib/image-compressor";
import { generateVideoThumbnail } from "../../../lib/video-thumbnail-generator";
import { putImage, deleteImage, putThumbnail } from "../../../db";
import { v4 as uuidv4 } from "uuid";
import Layout from "../../../components/template/Layout";
import Dropdown, {
  DropdownOption,
} from "../../../components/template/Drowpdown/dropdown";
import AutoResizeTextarea from "../../../components/template/TextArea/AutoResizeTextarea";
import CollapsibleSection from "../../../components/template/CollapsibleSection";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../../redux/store";
import { debounce } from "lodash";
import {
  updateFormField as updateReduxFormField,
  clearFormData,
  setPackages,
  setLocations,
  selectPackageOptions,
  selectLocationOptions,
  getFormData,
} from "../../../redux/slices/inboundItemFormSlice";
import {
  createInboundItem,
  selectHouseLocations,
  getHouseLocations,
  getEvents,
} from "../../../redux/slices/InboundItemsSlice";
import { fetchInspectionTemplates } from "../../../redux/slices/inspectionTemplatesSlice";
import { showToaster } from "../../../redux/slices/toasterSlice";
import { generateYearsOptions } from "../../../utils/common.ts";
import {
  getAccountPackagesandLocations,
  searchAllAccounts,
} from "../../../redux/services/InboundItemsAPI";
import AsyncDropdown from "../../../components/template/Drowpdown/AsyncDropdown";
import { isReallyOnline } from "../../../lib/isOnline.ts";
import { ItemStatus } from "../../../types/enums/item";
import { FaSpinner } from "react-icons/fa";
import { EventStatus } from "../../../types/enums/event";
import { FeatureFlagWrapper } from "../../../components/FeatureFlag/FeatureFlagWrapper";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";

const initialFormState = {
  description: "",
  media: [] as MediaItem[],
  assetNumber: "",
  stockNumber: "",
  vinSn: "",
  hours: 0,
  details: "",
  inspection: undefined,
  quantity: 1,
  requireInspection: false,
  createdBy_ID: "",
  createdBy_Name: "",
  year: null as number | null,
  make: "",
  model: "",
  status_ID: ItemStatus.Pending,
};

const CreateItem: React.FC = () => {
  // isLoading removed - using isInitializing instead
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const [mediaUploadProgress, setMediaUploadProgress] = useState<{
    completed: number;
    mediaTotal: number;
  }>({ completed: 0, mediaTotal: 0 });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const dispatch = useDispatch<AppDispatch>();
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const packageOptions = useSelector((state: RootState) =>
    selectPackageOptions(state)
  );
  const locationOptions = useSelector((state: RootState) =>
    selectLocationOptions(state)
  );
  const eventOptions = useSelector(
    (state: RootState) => state.inboundItems.events
  );
  const [productFamilyOptions, setProductFamilyOptions] = useState<
    DropdownOption[]
  >([]);
  // categoryOptions now memoized below
  // yearsOptions now memoized below
  const [isFullscreenView, setIsFullscreenView] = useState(false);
  const [fullscreenMediaIndex, setFullscreenMediaIndex] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [mediaUuidToDelete, setMediaUuidToDelete] = useState<string | null>(
    null
  );
  const [newItemForm, setNewItemForm] = useState<InboundItem>(
    initialFormState as InboundItem
  );
  const navigate = useNavigate();

  const { inspectionTemplates } = useSelector(
    (state: RootState) => state.inspectionTemplates
  );
  const { token, userId, userName } = useSelector(
    (state: RootState) => state.auth
  );
  const reduxFormData = useSelector((state: RootState) => getFormData(state));
  const houseLocations = useSelector((state: RootState) =>
    selectHouseLocations(state)
  );
  const enableUnstableFeatures = useFeatureFlag("enableUnstableFeatures");

  // Initialize form with fresh state on mount
  useEffect(() => {
    setIsInitializing(true);

    if (reduxFormData && Object.keys(reduxFormData).length > 0) {
      console.log("Restoring form data from Redux:", reduxFormData);
      setNewItemForm((prev) => ({ ...prev, ...reduxFormData }));
    }
    // Always set house locations as default
    if (houseLocations && houseLocations.length > 0) {
      dispatch(setLocations(houseLocations));
    }

    if (reduxFormData.consignorId) {
      handleGetPackagesAndLocations(reduxFormData.consignorId as number);
    }
    fetchLatestData();
    setTimeout(() => {
      setIsInitializing(false);
    }, 100);
  }, []);

  // Refresh reference data only (categories, locations, events). Does not clear form.
  const refreshReferenceData = async () => {
    const isOnline = await isReallyOnline();
    if (!isOnline) return;
    try {
      await dispatch(fetchInspectionTemplates({ token: token || "" }));
      await dispatch(getHouseLocations());
      if (enableUnstableFeatures) {
        dispatch(getEvents());
      }
    } catch (error) {
      console.error("Error refreshing reference data:", error);
    }
  };

  // Fetch latest data from server on load
  const fetchLatestData = async () => {
    await refreshReferenceData();
  };

  // Pull-to-refresh: refresh dropdown options only; form state is preserved
  const handleRefresh = async () => {
    await refreshReferenceData();
  };

  useEffect(() => {
    return () => {
      handleFormCleanup();
    };
  }, [dispatch]);

  // Memoize expensive category processing
  const categoryOptions = useMemo(() => {
    const map = new Map<number, string>();
    inspectionTemplates.forEach((template) => {
      if (template.categoryID && template.categoryDescription) {
        map.set(template.categoryID, template.categoryDescription);
      }
    });

    let categories = Array.from(map.entries()).map(([categoryId, label]) => ({
      value: String(categoryId),
      label,
    }));

    return categories.sort((a, b) => a.label.localeCompare(b.label));
  }, [inspectionTemplates]);

  // Memoize years options
  const yearsOptions = useMemo(() => generateYearsOptions(), []);

  const handleFormCleanup = async () => {
    const nextPath = window.location.pathname;
    const isGoingToListPage =
      nextPath === "/inbound-items" || nextPath === "/inbound-items/";
    const isGoingToMediaGallery = nextPath.includes("/media-gallery");
    const isStayingInCreateFlow =
      nextPath.includes("/create-item") || nextPath.includes("/media-gallery");

    console.log("Cleanup check:", {
      nextPath,
      isGoingToListPage,
      isGoingToMediaGallery,
      isStayingInCreateFlow,
    });

    // Only cleanup Redux form data if going to list page AND not staying in the create item flow
    if (isGoingToListPage && !isStayingInCreateFlow) {
      console.log("Performing Redux form cleanup - leaving create flow");
      dispatch(clearFormData());
    } else {
      console.log("Skipping Redux cleanup - staying in create flow");
    }
  };

  // Keep the redux form updated
  const persistReduxFormField = useCallback(
    debounce((field: keyof InboundItem, value: any) => {
      dispatch(updateReduxFormField({ field, value }));
    }, 500),
    [dispatch]
  );

  // Non-debounced batch update for multiple fields at once
  const persistReduxFormFields = (updates: Partial<InboundItem>) => {
    Object.entries(updates).forEach(([key, value]) => {
      dispatch(
        updateReduxFormField({ field: key as keyof InboundItem, value })
      );
    });
  };

  const validateField = (field: string, value: string | number): string => {
    switch (field) {
      case "description":
        if (!value) {
          return "Please provide a description";
        }
        break;
      default:
        return "";
    }
    return "";
  };

  const updateFormField = (field: keyof InboundItem, value: any) => {
    setNewItemForm((prev) => ({ ...prev, [field]: value }));
    persistReduxFormField(field, value);
  };

  const updateFormFields = (updates: Partial<InboundItem>) => {
    setNewItemForm((prev) => ({ ...prev, ...updates }));
    persistReduxFormFields(updates); // Use non-debounced batch update
  };

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    updateFormField(name as keyof InboundItem, value);

    // Validate the field and update form errors
    const error = validateField(name, value);
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));
  };

  const handleTextAreaChange = (value: string, field: string) => {
    updateFormField(field as keyof InboundItem, value);

    // Validate the field and update form errors
    const error = validateField(field, value);
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [field]: error,
    }));
  };

  // Helper function to convert file to base64

  // Add this function to any component or utility file

  // Helper method to process images with batching to prevent browser crashes
  // Processing 40+ images concurrently causes memory exhaustion and "Aw, snap!" crashes
  // Batching prevents this by limiting concurrent Web Workers and IndexedDB operations
  const processImages = async (imageFiles: File[]): Promise<MediaItem[]> => {
    // Process one at a time (BATCH_SIZE = 1) to avoid worker kill on Android when notifications arrive
    const BATCH_SIZE = 1;
    
    console.log(`📦 Processing ${imageFiles.length} images in batches of ${BATCH_SIZE}`);

    const newMediaItems: MediaItem[] = [];

    // Process in batches to avoid overwhelming memory with too many Web Workers
    // Each compression creates a Web Worker, and too many concurrent workers cause crashes
    for (let i = 0; i < imageFiles.length; i += BATCH_SIZE) {
      const batch = imageFiles.slice(i, i + BATCH_SIZE);
      
      // Process batch concurrently
      const batchPromises = batch.map(async (file) => {
        const uuid = uuidv4();
        const compressedFile = await compressImage(file);
        await putImage(uuid, compressedFile);
        
        setMediaUploadProgress(
          (prev: { completed: number; mediaTotal: number }) => ({
            ...prev,
            completed: prev.completed + 1,
          })
        );

        return {
          url: "",
          type: "image",
          type_ID: 1,
          id: uuid,
          uuid: uuid,
          uploaded: false,
        } as MediaItem;
      });

      const batchResults = await Promise.all(batchPromises);
      newMediaItems.push(...batchResults);
      
      // Small delay between batches to allow garbage collection and memory recovery
      if (i + BATCH_SIZE < imageFiles.length) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }

    return newMediaItems;
  };

  // Helper method to process videos - always sequential (needs thumbnail generation)
  const processVideos = async (videoFiles: File[]): Promise<MediaItem[]> => {
    const newMediaItems: MediaItem[] = [];
    
    for (const file of videoFiles) {
      const uuid = uuidv4();
      await putImage(uuid, file);

      try {
        const thumbnailBlob = await generateVideoThumbnail(file, 1);
        await putThumbnail(uuid, thumbnailBlob);
      } catch (error) {
        console.warn("Failed to generate thumbnail for video:", error);
        // Continue anyway - we'll show a default video icon
      }

      newMediaItems.push({
        url: "",
        type: "video",
        type_ID: 2,
        id: uuid,
        uuid: uuid,
        uploaded: false,
      });

      setMediaUploadProgress(
        (prev: { completed: number; mediaTotal: number }) => ({
          ...prev,
          completed: prev.completed + 1,
        })
      );
    }

    return newMediaItems;
  };

  const handleMediaUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    setMediaUploadProgress({ completed: 0, mediaTotal: files.length });
    setIsUploadingMedia(true);

    try {
      // Filter valid files and track invalid ones
      const validFiles: File[] = [];

      for (const file of Array.from(files)) {
        const fileExtension = file.name.split(".").pop()?.toLowerCase();
        const isImage =
          ["png", "jpeg", "jpg"].includes(fileExtension || "") &&
          ["image/png", "image/jpeg"].includes(file.type);
        const isVideo =
          ["mp4", "mov"].includes(fileExtension || "") &&
          ["video/mp4", "video/quicktime"].includes(file.type);

        if (isImage || isVideo) {
          validFiles.push(file);
        } else {
          console.error(`Invalid file type: ${file.name}`);
          dispatch(
            showToaster({
              message: `Invalid file type. Only PNG, JPEG, JPG, MP4, and MOV files are supported.`,
              type: "error",
            })
          );
          setIsUploadingMedia(false);
          event.target.value = ""; // Clear input so user can try again
          return;
        }
      }

      if (validFiles.length === 0) {
        setIsUploadingMedia(false);
        event.target.value = ""; // Clear input
        return;
      }

      // Create media items with blob storage (not uploaded yet)
      const newMediaItems: MediaItem[] = [];

      // Separate videos and images for different processing
      const videoFiles: File[] = [];
      const imageFiles: File[] = [];

      for (const file of validFiles) {
        if (file.type.startsWith("video/")) {
          videoFiles.push(file);
        } else {
          imageFiles.push(file);
        }
      }

      // Process images using the helper method (concurrent or sequential)
      const imageResults = await processImages(imageFiles);
      newMediaItems.push(...imageResults);

      // Process videos using the helper method (always sequential - needs thumbnail generation)
      const videoResults = await processVideos(videoFiles);
      newMediaItems.push(...videoResults);

      setNewItemForm((prev) => ({
        ...prev,
        media: [...(newItemForm?.media || []), ...newMediaItems],
      }));
      persistReduxFormField("media" as keyof InboundItem, [
        ...(newItemForm?.media || []),
        ...newMediaItems,
      ]);
    } catch (error) {
      console.error("Error processing media:", error);
    } finally {
      setIsUploadingMedia(false);
      // Clear the input value so the same file can be selected again
      event.target.value = "";
    }
  };

  const removeMedia = async (uuid: string) => {
    // Find the media item being removed by UUID
    const mediaItem = newItemForm?.media?.find(
      (media) => media.uuid === uuid || media.id === uuid
    );

    // Delete blob from IndexedDB if it exists and hasn't been uploaded
    if (mediaItem?.uuid && !mediaItem?.url) {
      try {
        await deleteImage(mediaItem.uuid);
        await deleteImage(`${mediaItem.uuid}_thumb`); // Also delete thumbnail
        console.log(`Deleted blob from IndexedDB: ${mediaItem.uuid}`);
      } catch (error) {
        console.warn(`Failed to delete blob ${mediaItem.uuid}:`, error);
      }
    }

    const newMedia =
      newItemForm?.media?.filter(
        (media) => media.uuid !== uuid && media.id !== uuid
      ) || [];
    setNewItemForm((prev) => ({ ...prev, media: newMedia }));
    persistReduxFormField("media" as keyof InboundItem, newMedia);
  };

  const requestDeleteMedia = (uuid: string) => {
    setMediaUuidToDelete(uuid);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteMedia = async () => {
    if (mediaUuidToDelete === null) return;

    await removeMedia(mediaUuidToDelete);

    setShowDeleteConfirm(false);
    setMediaUuidToDelete(null);

    // Close fullscreen if no more media
    const remainingMedia = newItemForm?.media || [];
    if (remainingMedia.length === 0) {
      setIsFullscreenView(false);
    }
  };

  const cancelDeleteMedia = () => {
    setShowDeleteConfirm(false);
    setMediaUuidToDelete(null);
  };

  const handleSaveChanges = async () => {
    setIsSaving(true);

    // Add isInbound flag only for API calls
    const tempId = await generateTempId();
    const params = {
      ...newItemForm,
      isInbound: true,
      tempId: tempId,
      id: -1,
      createdBy_ID: userId || "",
      createdBy_Name: userName || "",
    };

    // Dispatch the action and wait for it to complete
    try {
      await dispatch(createInboundItem(params)).unwrap();

      // Small delay to ensure user sees the saving state
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Form data will be cleared by cleanup effect when unmounting
      navigate("/inbound-items");
    } catch (error) {
      console.error("Failed to create item:", error);
      navigate("/inbound-items");
    }
    setIsSaving(false);
  };

  const generateTempId = () => {
    // Generate a random negative ID using crypto
    const randomBytes = new Uint32Array(1);
    crypto.getRandomValues(randomBytes);
    return randomBytes[0];
  };

  const handleSelectProductFamily = (option: DropdownOption) => {
    updateFormField("familyId" as keyof InboundItem, parseInt(option.value));
  };

  const generateProductFamilyList = useCallback(
    (categoryId: number) => {
      let families = inspectionTemplates.filter(
        (i) =>
          i.tenant_ID === 101 &&
          i.description !== null &&
          i.description.length > 0 &&
          i.categoryID === Number(categoryId)
      );

      let list: { value: string; label: string }[] = [];

      families.forEach((i) => {
        list.push({
          value: String(i.id),
          label: i.description,
        });
      });
      return list;
    },
    [inspectionTemplates]
  );

  const handleSelectCategory = (option: DropdownOption) => {
    updateFormField("categoryId", parseInt(option.value));

    if (option.value !== "0") {
      const productFamilyList = generateProductFamilyList(
        parseInt(option.value)
      );
      setProductFamilyOptions(productFamilyList);
    } else {
      setProductFamilyOptions([]);
    }
  };

  const handleViewMedia = (selectedMedia: MediaItem) => {
    const media = newItemForm?.media || [];
    const index = media.findIndex((m) => m.id === selectedMedia.id);
    setFullscreenMediaIndex(index >= 0 ? index : 0);
    setIsFullscreenView(true);
  };

  const handleViewAllMedia = () => {
    const media = newItemForm?.media || [];
    const params = {
      media: media,
      itemTitle: newItemForm.description || "Media Gallery",
    };
    navigate(`/inbound-items/create-item/media-gallery`, {
      state: params,
    });
  };

  const closeFullscreenView = () => {
    setIsFullscreenView(false);
  };

  const goToGallery = () => {
    setIsFullscreenView(false);
    handleViewAllMedia();
  };

  const nextMedia = () => {
    const media = newItemForm?.media || [];
    if (media.length > 1) {
      setFullscreenMediaIndex((prev) => (prev + 1) % media.length);
    }
  };

  const prevMedia = () => {
    const media = newItemForm?.media || [];
    if (media.length > 1) {
      setFullscreenMediaIndex(
        (prev) => (prev - 1 + media.length) % media.length
      );
    }
  };

  // Delete functions removed - functionality moved to media gallery

  const canSaveChanges = () => {
    if (!newItemForm.description) {
      return false;
    }
    if (mediaUploadProgress.completed < mediaUploadProgress.mediaTotal) {
      return false;
    }
    return true;
  };

  const handleSearchConsignors = async (searchTerm: string) => {
    if (await isReallyOnline()) {
      const response = await searchAllAccounts(token || "", {
        searchString: searchTerm,
      });
      return response.data.map((item: any) => ({
        value: item.id.toString(),
        label: item.accountName,
        consignorCode: item.consignorCode,
      }));
    }
    return [];
  };

  const handleSelectConsignor = (option: any) => {
    updateFormFields({
      consignorId: parseInt(option.value),
      consignor: option.label,
      consignorCode: option.consignorCode,
      packageId: 0,
      package: "",
      locationId: 0,
      location: "",
    });
    handleGetPackagesAndLocations(parseInt(option.value));
  };

  const handleClearConsignor = () => {
    updateFormFields({
      consignorId: 0,
      consignor: "",
      packageId: 0,
      package: "",
      locationId: 0,
      location: "",
    });

    // Clear Redux state (raw data)
    dispatch(setPackages([]));
    dispatch(setLocations(houseLocations));

    // Local state will be cleared via useEffect watching Redux
  };

  const handleGetPackagesAndLocations = async (account_ID: number) => {
    const isOnline = await isReallyOnline();
    if (!isOnline) return;
    try {
      const response = await getAccountPackagesandLocations(token || "", {
        accountId: account_ID,
      });
      dispatch(setPackages(response.data.packages));

      // Use consignor locations if available, otherwise fall back to house locations
      const locations =
        response.data.locations && response.data.locations.length > 0
          ? response.data.locations
          : houseLocations;
      dispatch(setLocations(locations));
    } catch (error) {
      console.error("Error getting packages and locations:", error);
      // On error, still use house locations
      if (houseLocations && houseLocations.length > 0) {
        dispatch(setLocations(houseLocations));
      }
    }
  };

  // Show loading state while fetching data or initializing
  if (isInitializing || !newItemForm) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-100">
        <TopBar title="Quick Create" showBackIcon={true} />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-t-transparent border-primary rounded-full animate-spin mx-auto mb-4"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <PullToRefresh
      onRefresh={handleRefresh}
      refreshingContent={
        <div className="flex justify-center items-center mt-4">
          <div className="w-8 h-8 border-4 border-t-transparent border-primary rounded-full animate-spin"></div>
        </div>
      }
    >
      <div className="bg-gray-100 min-h-screen">
        <Layout title="Quick Create" showBackIcon={true}>
        <div className="p-4 pb-[140px]">
          <div>
            <input
              type="text"
              className="w-full p-3 border border-gray-300 rounded-md text-sm"
              placeholder="Title/Lead Description"
              name="description"
              value={newItemForm?.description || ""}
              onChange={handleInputChange}
            />
            {formErrors.description && (
              <p className="text-red-500 text-sm mt-1 ml-1">
                {formErrors.description}
              </p>
            )}
          </div>
          <div className="mt-4">
            <AutoResizeTextarea
              value={newItemForm?.details || ""}
              className="p-3"
              onChange={(val: any) => handleTextAreaChange(val, "details")}
              placeholder="Details"
              name="details"
            />
            {formErrors.details && (
              <p className="text-red-500 text-sm mt-1 ml-1">
                {formErrors.details}
              </p>
            )}
          </div>

          {/* Media Section */}
          <MediaUploadSection
            media={newItemForm?.media || []}
            onMediaUpload={handleMediaUpload}
            mediaUploadProgress={mediaUploadProgress}
            onMediaRemove={(e, uuid) => {
              e.stopPropagation();
              requestDeleteMedia(uuid);
            }}
            onViewMedia={handleViewMedia}
            onViewAllMedia={handleViewAllMedia}
            isUploadingMedia={isUploadingMedia}
            allowUpload={true}
          />
          <CollapsibleSection
            title="Consignor Information"
            defaultExpanded={false}
          >
            <div className="">
              <AsyncDropdown
                onSearch={handleSearchConsignors}
                emptyMessage="No consignors found"
                loadingMessage="Loading consignors..."
                placeholder="Select Consignor"
                onSelect={(option) => handleSelectConsignor(option)}
                onClear={handleClearConsignor}
                value={newItemForm?.consignor || ""}
                minSearchLength={2}
                searchDebounceMs={1000}
                allowClear={true}
              />
              {formErrors.consignorId && (
                <p className="text-red-500 text-sm mt-1 ml-1">
                  {formErrors.consignorId}
                </p>
              )}
            </div>

            <div className="mt-4">
              <Dropdown
                options={packageOptions}
                isSearchable={true}
                placeholder="Select Package"
                allowClear={true}
                onClear={() =>
                  handleInputChange({
                    target: { name: "packageId", value: null },
                  })
                }
                value={newItemForm?.package || ""}
                disabled={!packageOptions || packageOptions.length === 0}
                onSelect={(option) => {
                  updateFormField("packageId", parseInt(option.value));
                  updateFormField("package", option.label);
                }}
              />
              {formErrors.packageId && (
                <p className="text-red-500 text-sm mt-1 ml-1">
                  {formErrors.packageId}
                </p>
              )}
            </div>
            <div className="mt-4">
              <input
                type="text"
                className="w-full p-3 border border-gray-300 rounded-md text-sm"
                placeholder="Consignor Code"
                name="consignorCode"
                value={newItemForm?.consignorCode || ""}
                onChange={handleInputChange}
                disabled={Boolean(newItemForm?.consignorId)}
              />
              {formErrors.consignorCode && (
                <p className="text-red-500 text-sm mt-1 ml-1">
                  {formErrors.consignorCode}
                </p>
              )}
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Location" defaultExpanded={false}>
            <div className="mt-4">
              <Dropdown
                options={locationOptions}
                isSearchable={true}
                placeholder="Select Location"
                allowClear={true}
                onClear={() =>
                  handleInputChange({
                    target: { name: "locationId", value: null },
                  })
                }
                value={
                  newItemForm?.locationId
                    ? (
                        locationOptions.find(
                          (option) =>
                            option.value === newItemForm.locationId?.toString()
                        ) as DropdownOption
                      )?.label || ""
                    : ""
                }
                disabled={!locationOptions || locationOptions.length === 0}
                onSelect={(option) => {
                  updateFormField("locationId", parseInt(option.value));
                  updateFormField("location", option.label);
                }}
              />
              {formErrors.locationId && (
                <p className="text-red-500 text-sm mt-1 ml-1">
                  {formErrors.locationId}
                </p>
              )}
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Item Data Points" defaultExpanded={false}>
            <div className="">
              <Dropdown
                options={[
                  { value: "0", label: "Select Category" },
                  ...categoryOptions,
                ]}
                isSearchable={true}
                placeholder="Category"
                onSelect={(option) => handleSelectCategory(option)}
                value={
                  newItemForm?.categoryId
                    ? newItemForm.categoryId?.toString()
                    : "0"
                }
              />
            </div>
            {productFamilyOptions.length > 0 && (
              <div className="mt-4">
                <Dropdown
                  options={[
                    { value: "0", label: "Select Product Family" },
                    ...productFamilyOptions,
                  ]}
                  isSearchable={true}
                  placeholder="Select Product Family"
                  onSelect={(option) => handleSelectProductFamily(option)}
                  value={newItemForm?.familyId?.toString() || ""}
                />
              </div>
            )}
            <div className="mt-4">
              <input
                type="text"
                className="w-full p-3 border border-gray-300 rounded-md text-sm"
                placeholder="Asset Number"
                name="assetNumber"
                value={newItemForm?.assetNumber || ""}
                onChange={handleInputChange}
              />
              {formErrors.assetNumber && (
                <p className="text-red-500 text-sm mt-1 ml-1">
                  {formErrors.assetNumber}
                </p>
              )}
            </div>
            <div className="mt-4">
              <input
                type="text"
                className="w-full p-3 border border-gray-300 rounded-md text-sm"
                placeholder="Stock Number"
                name="stockNumber"
                value={newItemForm?.stockNumber || ""}
                onChange={handleInputChange}
              />
              {formErrors.stockNumber && (
                <p className="text-red-500 text-sm mt-1 ml-1">
                  {formErrors.stockNumber}
                </p>
              )}
            </div>
            <div className="mt-4">
              <input
                type="text"
                className="w-full p-3 border border-gray-300 rounded-md text-sm"
                placeholder="VIN"
                name="vinSn"
                value={newItemForm?.vinSn || ""}
                onChange={handleInputChange}
              />
              {formErrors.vinSn && (
                <p className="text-red-500 text-sm mt-1 ml-1">
                  {formErrors.vinSn}
                </p>
              )}
            </div>
            <div className="mt-4">
              <Dropdown
                options={yearsOptions}
                isSearchable={true}
                placeholder="Year"
                value={newItemForm?.year?.toString() || ""}
                onSelect={(option) =>
                  handleInputChange({
                    target: { name: "year", value: option.value },
                  })
                }
              />
              {formErrors.year && (
                <p className="text-red-500 text-sm mt-1 ml-1">
                  {formErrors.year}
                </p>
              )}
            </div>
            <div className="mt-4">
              <input
                type="text"
                placeholder="Make"
                name="make"
                className="w-full p-3 border border-gray-300 rounded-md text-sm"
                value={newItemForm?.make?.toString() || ""}
                onChange={handleInputChange}
              />
              {formErrors.make && (
                <p className="text-red-500 text-sm mt-1 ml-1">
                  {formErrors.make}
                </p>
              )}
            </div>
            <div className="mt-4">
              <input
                type="text"
                className="w-full p-3 border border-gray-300 rounded-md text-sm"
                placeholder="Model"
                name="model"
                value={newItemForm?.model?.toString() || ""}
                onChange={handleInputChange}
              />
              {formErrors.model && (
                <p className="text-red-500 text-sm mt-1 ml-1">
                  {formErrors.model}
                </p>
              )}
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Notes" defaultExpanded={false}>
            <div className="">
              <p className="text-gray-500 text-xs">For Internal Use Only</p>
              <AutoResizeTextarea
                value={newItemForm?.notes || ""}
                className="p-3"
                onChange={(val: any) => handleTextAreaChange(val, "notes")}
                placeholder="Add any additional notes..."
                name="notes"
              />
              {formErrors.notes && (
                <p className="text-red-500 text-sm mt-1 ml-1">
                  {formErrors.notes}
                </p>
              )}
            </div>
          </CollapsibleSection>

          <FeatureFlagWrapper flag="enableUnstableFeatures">
            <CollapsibleSection
              title="Event Information"
              defaultExpanded={false}
            >
              <div className="mt-4">
                <Dropdown
                  options={eventOptions.map((option) => ({
                    value: option.value,
                    label:
                      option.label +
                      (option.eventStatus_ID === EventStatus.In_Progress
                        ? " (In Progress)"
                        : option.isRunOrderLocked
                        ? " (Run Order Locked)"
                        : ""),
                    disabled:
                      option.eventStatus_ID === EventStatus.In_Progress ||
                      option.isRunOrderLocked,
                  }))}
                  isSearchable={true}
                  allowClear={true}
                  onClear={() =>
                    handleInputChange({
                      target: { name: "eventId", value: undefined },
                    })
                  }
                  placeholder="Select Event"
                  value={newItemForm?.eventId?.toString() || ""}
                  onSelect={(option) =>
                    handleInputChange({
                      target: { name: "eventId", value: option.value },
                    })
                  }
                />
                {formErrors.eventId && (
                  <p className="text-red-500 text-sm mt-1 ml-1">
                    {formErrors.eventId}
                  </p>
                )}
              </div>
              <div className="mt-4">
                <input
                  type="text"
                  className="w-full p-3 border border-gray-300 rounded-md text-sm"
                  placeholder="Lot Number"
                  name="lotNumber"
                  value={newItemForm?.lotNumber?.toString() || ""}
                  onChange={handleInputChange}
                  disabled={Boolean(!newItemForm?.eventId)}
                />
                {formErrors.lotNumber && (
                  <p className="text-red-500 text-sm mt-1 ml-1">
                    {formErrors.lotNumber}
                  </p>
                )}
              </div>
            </CollapsibleSection>
          </FeatureFlagWrapper>
        </div>
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-white rounded-t-2xl">
          <button
            className={
              "w-full bg-primary text-white rounded-md p-3 shadow-button-shadow" +
              (canSaveChanges() ? "" : " opacity-50")
            }
            onClick={handleSaveChanges}
            disabled={isInitializing || isUploadingMedia || !canSaveChanges()}
          >
            {isSaving ? (
              <div className="py-1 flex items-center justify-center">
                <FaSpinner className="animate-spin mr-1" />
                Saving...
              </div>
            ) : (
              "Save"
            )}
          </button>
        </div>
      </Layout>

      {/* Fullscreen Media Overlay */}
      {isFullscreenView &&
        newItemForm?.media &&
        newItemForm.media.length > 0 && (
          <MediaViewer
            media={newItemForm.media}
            currentIndex={fullscreenMediaIndex}
            onClose={closeFullscreenView}
            onNext={nextMedia}
            onPrev={prevMedia}
            onViewGallery={goToGallery}
            onDelete={() => {
              const media = newItemForm?.media || [];
              const currentMedia = media[fullscreenMediaIndex];
              if (currentMedia) {
                const uuid = currentMedia.uuid || currentMedia.id || "";
                if (uuid) {
                  requestDeleteMedia(uuid);
                }
              }
            }}
          />
        )}

      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Delete Media"
        message="Are you sure you want to delete this media? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDeleteMedia}
        onCancel={cancelDeleteMedia}
      />
      </div>
    </PullToRefresh>
  );
};

export default CreateItem;
